/**
 * WEB-2057 — пакет строк воронки для одного языка.
 *
 * Пакеты лежат по файлу на язык и приезжают ЛЕНИВО, по языку конкретной
 * воронки: раньше все 29 неосновных языков жили одним файлом на 280 КБ
 * несжатых, и покупатель испанской воронки скачивал ещё и тайский с ивритом.
 * Содержимое перенесено ДОСЛОВНО, ни одна фраза не менялась.
 */

import type { PublicLocalePack } from '../publicQuizStrings';
import { pack as basePack } from './pt';
import { derivePack } from '../publicQuizStrings';

/**
 * Бразильский португальский. Отличий от европейского немного, но они на самом
 * виду: герундий вместо «a + инфинитив» («Carregando…», а не «A carregar…»),
 * «app» вместо «aplicação», «link» вместо «ligação», «Meta» вместо «Objetivo».
 * Поэтому наследник с точечными заменами, а не копия целого пакета: общие
 * фразы иначе разъехались бы при первой же правке одной из двух копий.
 */
export const pack: PublicLocalePack = derivePack(basePack, {
  crashTitle: 'Algo deu errado',
  intlTag: 'pt-BR',
  redirectingIn: 'Redirecionando em {n} s…',
  // WEB-2975: «aba» e «app», não «separador» e «aplicação».
  purchaseThanks: {
    heading: 'Obrigado pela sua compra!',
    body:
      'Seu pagamento de {name} foi concluído com sucesso. Enviamos os detalhes para o seu e-mail.',
    appReturn:
      'Volte ao app para continuar — seu acesso está sendo ativado.',
    closeTab: 'Você já pode fechar esta aba.',
    // WEB-2977: páginas depois do pagamento — «app», «link» e gerúndio, como
    // no resto do pacote brasileiro; o que não muda vem do europeu.
    handoff: {
      ...basePack.purchaseThanks.handoff,
      downloadApp: 'Baixar o app',
      openApp: 'Abrir o app',
      openAppAlt: 'Abrir o app (outra forma)',
      enterCodeManually: 'Ou digite o código manualmente:',
      openingApp: 'Abrindo o app… Se ele não estiver instalado, a loja será aberta.',
      copyFailed: 'Não foi possível copiar — selecione o código manualmente',
      loading: 'Carregando…',
      pendingHeading: 'Processando sua compra',
      pendingBody: 'Estamos ativando seu acesso. Normalmente leva alguns segundos.',
      confirmationDelayedBody: 'Ainda estamos aguardando a confirmação do seu pagamento. Isso acontece quando o provedor de pagamentos demora para responder. Tente novamente em instantes.',
      sessionNotFoundBody: 'Este link não é válido ou já foi usado. Se você acha que isso é um erro, entre em contato com o suporte.',
      loadFailedBody: 'Verifique sua conexão e tente novamente. Se continuar acontecendo, aguarde um pouco — o serviço pode estar temporariamente sobrecarregado.',
      resendSentHeading: 'Um novo link foi enviado',
      resendSentBody: 'Confira seu e-mail.',
      resendRateLimited: 'O e-mail foi enviado há pouco — pode levar alguns minutos para chegar. Você poderá enviá-lo novamente mais tarde.',
      resendNotConfigured: 'Não foi possível enviar o e-mail agora. Entre em contato com o suporte deste projeto — seu acesso será restaurado manualmente.',
      resendAgain: 'Enviar o link novamente',
      resendInline: 'Não recebeu o e-mail? Enviar novamente',
      linkAlreadyUsed: 'Este link de acesso já foi usado',
      linkExpired: 'Este link de acesso expirou',
      newLinkToEmail: 'Podemos enviar um novo link para {email}.',
      newLinkToYourEmail: 'Podemos enviar um novo link para o seu e-mail.',
      cancelBody: 'Você cancelou o pagamento. Se mudar de ideia, volte e tente novamente — nada do que você preencheu foi perdido.',
      cancelNoHistoryHint: 'Não conseguimos identificar de onde você veio — feche esta aba ou abra a página de pagamento novamente.',
      magicInvalidHeading: 'Este link não é válido',
      magicInvalidBody: 'Isto não parece um link de acesso real ao app. Se você o recebeu no e-mail após a compra, abra esse e-mail novamente e siga o link em um dispositivo com o app instalado.',
      magicHeading: 'Link de acesso ao app',
      magicBody: 'Abra-o em um dispositivo com o app instalado — o app vai ativar o acesso com o código abaixo. Se o app ainda não estiver instalado, instale-o primeiro e depois abra este link novamente. O link do e-mail é válido por tempo limitado; se o acesso não abriu, abra o e-mail mais uma vez.',
    },
  },
  loaderTitle: 'Estamos analisando suas respostas...',
  loaderSteps: [
    'Processando suas respostas',
    'Calculando seu resultado',
    'Preparando seu plano',
  ],
  chartEndLabel: 'Meta',
  nowGoalGoalLabel: 'Sua meta',
  blurTeaserLockedLabel: 'oculto até a compra',
  invalidEmailWarning: 'Confira seu endereço de e-mail',
  loaderSkipLabel: 'Pular',
  returnToAppFallback: 'Voltar para o app',
  openingAppHint:
    'Abrindo o app… Se ele não estiver instalado, a loja será aberta.',
  loadingLabel: 'Carregando...',
  completedText: 'Você concluiu o quiz.',
  consentMessage:
    'Usamos cookies e tecnologias semelhantes para análise e publicidade. Com o seu consentimento, compartilhamos dados sobre suas ações com redes de anúncios (como Meta e TikTok) para medir e direcionar anúncios. Você pode aceitar ou recusar.',
  // WEB-2145 п.5: подпись ссылки на политику у Бразилии и Португалии
  // совпадает («Política de Privacidade») и наследуется от PT; расходится
  // только эта фраза — бразильское «sua» против европейского «a sua».
  consentRecallLabel: 'Alterar sua decisão sobre cookies',
  cookieBlockMessage: 'Usamos cookies para melhorar sua experiência.',
  checkoutUnavailable:
    'Os pagamentos estão temporariamente indisponíveis. Tente novamente mais tarde.',
  checkoutPreviewOnly:
    'Isto é uma pré-visualização — o pagamento real está desativado',
  requiredFieldWarning: 'Este campo é obrigatório',
  // WEB-1907 (добор 2): бразильская разница в двух плейсхолдерах; «Concordo»,
  // «Preço» и «Pagar com cartão» совпадают с Португалией и приходят из PT.
  emailInputPlaceholder: 'Digite seu e-mail',
  textInputPlaceholder: 'Digite aqui...',
  // WEB-2158: строка плеера под галочками согласия. Бразильская форма
  // расходится с европейской дважды («salva» против «guardada», «marcar»
  // против «assinalar»), поэтому перекрываем её, а не наследуем от PT.
  sensitiveConsentNote:
    'Você pode continuar sem marcar esta caixa — a resposta simplesmente não será salva.',
  /**
   * Ревизия пачки 16 (находка Ф6): УМОЛЧАНИЯ СОДЕРЖИМОГО тоже бразильские.
   * Надписи плеера у pt-BR были свои с самого начала, а `blockDefaults`
   * наследовались от Португалии целиком — покупатель в Бразилии читал в
   * содержимом воронки «isto interessa-lhe?», «o seu IMC», «A analisar os
   * seus dados». Перекрываем РАЗНИЦУ, а не словарь заново: где формы
   * совпадают (bmiZoneNormal, microFeedbackTitle, metricLabel), ключ
   * по-прежнему приходит из PT и правка Португалии доедет до Бразилии сама.
   */
  blockDefaults: {
    ...basePack.blockDefaults,
    selfRelevanceQuestion: 'Até que ponto isso tem a ver com você?',
    scratchRevealNote: 'Continue para a próxima etapa',
    blurTeaserTitle: 'Sua análise está pronta',
    forecastTitle: 'Sua previsão',
    promoTicketTitle: 'Seu código promocional',
    bmiTitle: 'Seu índice de massa corporal',
    bmiValueLabel: 'Seu IMC',
    preferenceQuestion: 'Isso te interessa?',
    preferenceYesLabel: 'Me interessa',
    loaderProcessingTitle: 'Analisando seus dados...',
    numberInputPlaceholder: 'Digite um número',
    ratingStarsLabel: 'Avalie sua experiência',
    // Бразильские притяжательные без артикля («minhas respostas sobre
    // minha saúde») против европейских «as minhas … a minha».
    sensitiveConsentCollect:
      'Dou meu consentimento para o tratamento das minhas respostas sobre minha saúde',
    sensitiveConsentShare:
      'Autorizo o compartilhamento dessas respostas com os parceiros deste serviço',
    nameFieldPlaceholder: 'Seu nome',
    photoUploadQuestion: 'Envie uma foto',
  },
  portal: {
    invalidLink: 'Esta página serve para gerenciar sua assinatura. Para entrar, use o link do seu e-mail ou peça um novo abaixo.',
    expiredCode: 'Este link de acesso já foi usado ou expirou — cada link enviado por e-mail funciona apenas uma vez. Peça um novo abaixo.',
    expiredManageLink: 'Este link de gerenciamento expirou. Peça um novo abaixo.',
    sessionExpired: 'Sua sessão expirou. Peça abaixo um novo link de acesso.',
    loading: 'Carregando as assinaturas…',
    loadError: 'Não foi possível carregar as assinaturas. Tente mais tarde.',
    empty: 'Não encontramos nenhuma assinatura gerenciada nesta página. Se você assinou pela App Store ou pelo Google Play, gerencie essa assinatura na loja onde comprou.',
    cancelError: 'Não foi possível cancelar a assinatura. Tente novamente.',
    cancelConfirmTitle: 'Tem certeza de que quer cancelar sua assinatura?',
    cancelConfirmKeep: 'Manter a assinatura',
    cancelConfirmProceed: 'Cancelar mesmo assim',
    resumeError: 'Não foi possível retomar a assinatura. Tente novamente.',
    resumePeriodEnded: 'Esta assinatura já terminou — o período pago acabou. Para voltar, faça uma nova assinatura.',
    resumeSuccess: 'Sua assinatura está ativa de novo e será renovada normalmente.',
    downsellError: 'Não foi possível aplicar o desconto. Tente novamente.',
    downsellNotEligible: 'Este desconto não pode ser aplicado enquanto a assinatura estiver pausada ou prestes a terminar. Retome ou faça uma nova para poder usá-lo.',
    downsellUnavailable: 'Este desconto não está disponível para a sua assinatura.',
    downsellSuccess: 'O desconto vale a partir do próximo período de cobrança.',
    entryPrompt: 'Informe o e-mail que você usou na compra e enviaremos um link de acesso.',
    entrySubmit: 'Enviar um link de acesso',
    entrySent: 'Se este e-mail estiver cadastrado, enviamos um link de acesso. Confira sua caixa de entrada.',
    entrySendError: 'Não foi possível enviar o link. Tente novamente.',
    entryNumberPrompt: 'Para gerenciar sua assinatura, entre na sua conta — enviaremos um link de acesso para o e-mail da compra.',
    entryNumberSubmit: 'Enviar link de acesso para {email}',
    entryNumberSent: 'Enviamos um link de acesso para {email}. Confira sua caixa de entrada e a pasta de spam.',
    entryNumberOtherEmail: 'Usar outro e-mail',
    listTitle: 'Suas assinaturas',
    cancelLabel: 'Cancelar a assinatura',
    resumeLabel: 'Retomar a assinatura',
    offerTitle: 'Espere — temos um desconto',
    offerCta: 'Aceitar o desconto',
    cancellationScheduled: 'Cancelamento agendado',
    subscriptionCanceled: 'Assinatura cancelada',
    newPrice: 'Novo preço: {amount}',
    cancellationScheduledOn: 'Cancelamento agendado para {date}',
    renewsOn: 'Renova em {date}',
    statusLabels: {
      active: 'Ativa',
      trialing: 'Teste grátis',
      past_due: 'Pagamento atrasado',
      unpaid: 'Não paga',
      canceled: 'Cancelada',
      incomplete: 'Incompleta',
      incomplete_expired: 'Expirada',
      paused: 'Pausada',
    },
  },
  /** WEB-2115: страница получателя ссылки предпросмотра. */
  previewLink: {
    gateTitle: 'Digite o código de acesso',
    gateHint:
      'O autor enviou um código curto para você, normalmente em uma mensagem separada.',
    gatePlaceholder: 'Código',
    gateOpen: 'Abrir',
    wrong: 'O código não funcionou',
    lockedTitle: 'Sem tentativas restantes',
    lockedSub: 'Peça ao autor para enviar um novo link.',
    deadTitle: 'Este link não funciona mais',
    deadSub: 'Peça ao autor para enviar um novo.',
    banner:
      'Pré-visualização: você está vendo uma versão que o autor enviou para revisão. Seu percurso não é contabilizado e o pagamento não será concluído.',
    versionOf: 'Versão {n} de {total}',
    versionPicker: 'Qual versão ver',
    versionPrev: 'Versão anterior',
    versionNext: 'Próxima versão',
    retryIn: 'Tente de novo em {minutes} min.',
  },
  systemPage: {
    page: ['Página não encontrada', 'Confira o endereço ou abra o link de novo.'],
    funnel: ['Página indisponível', 'No momento ela não pode ser aberta. Volte mais tarde.'],
    quiz: ['Quiz não encontrado', 'O link pode estar desatualizado. Peça um novo para quem enviou.'],
    paywall: ['Oferta não encontrada', 'O link pode estar desatualizado. Peça um novo para quem enviou.'],
    service: ['Serviço indisponível', 'Tente de novo em alguns minutos.'],
    author: ['Este link é para o autor', 'Entre na sua conta neste navegador e abra o link de novo.'],
    signIn: 'Entrar',
  },
});
